import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { admins, mainAdmin } from '../../config/config';
import { CHAIN_ID, normalizeChainId } from '../../utils/connectionConfig';
import {
  getAccounts,
  getChainId,
  requestAccounts,
  revokeAccountsPermission,
  switchToTargetChain,
} from '../../utils/connectWallet';
import {
  setActiveProvider,
  waitForInjectedProvider,
} from '../../utils/walletProvider';
import {
  connectWalletConnect,
  disconnectWalletConnect,
  restoreWalletConnect,
} from '../../utils/walletConnect';

/**
 * Единственное место, где живёт состояние подключения.
 *
 * Раньше оно было заперто внутри кнопки: форма отправки не могла узнать, кто
 * подключён и в какой сети, и обнаруживала проблему только когда транзакция
 * уже уходила в кошелёк. Здесь же — подписка на смену аккаунта и сети, чтобы
 * показанный адрес всегда совпадал с тем, от чьего имени уйдёт подпись.
 *
 * Кошельков два вида: расширение в браузере и WalletConnect (приложение на
 * телефоне или QR). Найденное расширение (`injected`) и кошелёк, через который
 * подключились (`wallet`), — разные вещи: на компьютере с MetaMask можно
 * подключиться и по QR, и тогда подписывать должен телефон.
 */
const WalletContext = createContext(null);

/**
 * Список из конфига — справочная информация, а не механизм доступа: контракт
 * всё равно проверяет права сам. Панель лишь предупреждает заранее, чтобы
 * оператор не узнавал о чужом кошельке из ревёрта, уже потратив газ.
 */
const KNOWN_ADMINS = new Set(
  [mainAdmin, ...admins].map((address) => address.toLowerCase()),
);

export const useWallet = () => {
  const value = useContext(WalletContext);

  if (!value) {
    throw new Error('useWallet вызван вне WalletProvider');
  }

  return value;
};

export const WalletProvider = ({ children }) => {
  const [injected, setInjected] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [kind, setKind] = useState(null);
  const [detecting, setDetecting] = useState(true);
  const [address, setAddress] = useState('');
  const [chainId, setChainId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Поиск кошелька и восстановление уже выданного доступа.
  useEffect(() => {
    let cancelled = false;

    const detect = async () => {
      const [provider, walletConnect] = await Promise.all([
        waitForInjectedProvider(),
        restoreWalletConnect(),
      ]);

      if (cancelled) return;

      setInjected(provider);
      setDetecting(false);

      if (walletConnect) {
        setWallet(walletConnect);
        setKind('walletconnect');
        setAddress(walletConnect.accounts[0] ?? '');
        setChainId(await getChainId(walletConnect));
        return;
      }

      if (!provider) return;

      // Разрешение в расширении выдаётся один раз и переживает перезагрузку
      // страницы. Спрашивать его заново — лишнее действие оператора после
      // каждого обновления списка.
      const [accounts, currentChainId] = await Promise.all([
        getAccounts(provider),
        getChainId(provider),
      ]);

      if (cancelled) return;

      setAddress(accounts[0] ?? '');
      setChainId(currentChainId);

      if (accounts.length > 0) {
        setWallet(provider);
        setKind('injected');
      }
    };

    detect();

    return () => {
      cancelled = true;
    };
  }, []);

  // Подпись берёт кошелёк из модуля, а не из контекста: она живёт в утилитах.
  useEffect(() => {
    setActiveProvider(wallet);
  }, [wallet]);

  // Смена аккаунта или сети прямо в кошельке при открытой панели. Пока ничего
  // не подключено, слушаем расширение: доступ могли выдать из него самого.
  const source = wallet ?? injected;

  useEffect(() => {
    if (!source?.on) return undefined;

    const handleAccountsChanged = (accounts) => {
      setAddress(accounts?.[0] ?? '');
      setError('');

      if (accounts?.length > 0 && !wallet) {
        setWallet(source);
        setKind('injected');
      }
    };

    const handleChainChanged = (nextChainId) => {
      setChainId(normalizeChainId(nextChainId));
      setError('');
    };

    // У WalletConnect это разрыв сессии из приложения кошелька: подписывать
    // больше нечем, кошелёк сбрасывается целиком.
    const handleDisconnect = () => {
      setAddress('');

      if (kind === 'walletconnect') {
        setWallet(null);
        setKind(null);
      }
    };

    source.on('accountsChanged', handleAccountsChanged);
    source.on('chainChanged', handleChainChanged);
    source.on('disconnect', handleDisconnect);

    return () => {
      source.removeListener?.('accountsChanged', handleAccountsChanged);
      source.removeListener?.('chainChanged', handleChainChanged);
      source.removeListener?.('disconnect', handleDisconnect);
    };
  }, [kind, source, wallet]);

  /** `via` — `'injected'` (расширение) или `'walletconnect'`. */
  const connect = useCallback(
    async (via = 'injected') => {
      // Второй клик по кнопке, пока открыт запрос, кошелёк отбрасывает с
      // ошибкой -32002 — и выглядит это как «подключение не работает».
      if (busy) return;

      setError('');

      if (via === 'injected' && !injected) {
        setError(
          'Расширение кошелька не найдено. Подключитесь через WalletConnect.',
        );
        return;
      }

      setBusy(true);

      try {
        let provider = injected;
        let accounts;

        if (via === 'walletconnect') {
          provider = await connectWalletConnect();
          accounts = provider.accounts;
        } else {
          accounts = await requestAccounts(provider);
        }

        if (!accounts?.length) {
          throw new Error('Кошелёк не вернул ни одного аккаунта');
        }

        setWallet(provider);
        setKind(via);
        setAddress(accounts[0]);

        const currentChainId = await getChainId(provider);
        setChainId(currentChainId);

        if (currentChainId !== CHAIN_ID) {
          setChainId(await switchToTargetChain(provider));
        }
      } catch (connectError) {
        setError(connectError.message);
      } finally {
        setBusy(false);
      }
    },
    [busy, injected],
  );

  const switchChain = useCallback(async () => {
    if (busy || !wallet) return;

    setError('');
    setBusy(true);

    try {
      setChainId(await switchToTargetChain(wallet));
    } catch (switchError) {
      setError(switchError.message);
    } finally {
      setBusy(false);
    }
  }, [busy, wallet]);

  const disconnect = useCallback(async () => {
    setError('');

    // Локального сброса мало: кошелёк продолжит считать сайт разрешённым, и
    // «отключение» окажется видимостью. Расширение просим отозвать доступ,
    // у WalletConnect рвём сессию.
    if (kind === 'walletconnect' && wallet) {
      await disconnectWalletConnect(wallet);
    } else if (wallet) {
      await revokeAccountsPermission(wallet);
    }

    setWallet(null);
    setKind(null);
    setAddress('');
  }, [kind, wallet]);

  const value = useMemo(() => {
    const isCorrectChain = chainId === CHAIN_ID;

    return {
      // Расширение в браузере. Без него остаётся WalletConnect — подключиться
      // можно всегда.
      hasInjected: Boolean(injected),
      walletKind: kind,
      detecting,
      address,
      chainId,
      isConnected: Boolean(address),
      isCorrectChain,
      isKnownAdmin: Boolean(address) && KNOWN_ADMINS.has(address.toLowerCase()),
      // Готовность к подписи — это доступ к аккаунту И правильная сеть.
      isReady: Boolean(address) && isCorrectChain,
      busy,
      error,
      connect,
      switchChain,
      disconnect,
    };
  }, [
    address,
    busy,
    chainId,
    connect,
    detecting,
    disconnect,
    error,
    injected,
    kind,
    switchChain,
  ]);

  return (
    <WalletContext.Provider value={value}>{children}</WalletContext.Provider>
  );
};

export default WalletProvider;
